import React, { useState, useEffect } from 'react';
import { FaFilePdf, FaFileExcel, FaFileWord } from 'react-icons/fa';
import { FinancialWithFiles, FinancialFileRecord } from './types'; // asegúrate que tus types.ts tiene los campos correctos
import {useCleanPath} from '../Globals/Hooks/useCleanPath';
import './styles.css';

const quarters = [
  { code: 'T1', label: 'Primer Trimestre' },
  { code: 'T2', label: 'Segundo Trimestre' },
  { code: 'T3', label: 'Tercer Trimestre' },
  { code: 'T4', label: 'Cuarto Trimestre' },
  { code: 'ANUAL', label: 'Anual' }
];

const GROUPS: Record<string, string> = {
  contable: 'Información contable',
  presupuestal: 'Información presupuestal',
  programatica: 'Información programática',
  informacionadicional: 'Información adicional',
  disciplinafinanciera: 'Disciplina financiera',
};
function normalizeQuarter(val: string) {
  if (!val) return "";
  let q = (val + '').trim().toUpperCase().replace(/\s/g, '');
  if (['1', '2', '3', '4'].includes(q)) return `T${q}`;
  if (['T1', 'T2', 'T3', 'T4', 'ANUAL'].includes(q)) return q;
  return q;
}
export const FinancialViewNav: React.FC = () => {
  const [report, setReport] = useState<FinancialWithFiles[]>([]);
  const [openYear, setOpenYear] = useState<number | null>(null);

  useEffect(() => {
    const data = window.FinancialViewData?.[0]?.data.reportFinancial;
    if (Array.isArray(data)) {
      setReport(data);
      const years = data.map(r => r.year)
        .filter((v, i, a) => a.indexOf(v) === i)
        .sort((a, b) => b - a);
      if (years.length) setOpenYear(years[0]);
    }
  }, []);

  const iconFor = (type: string) =>
    type === 'XLSX'
      ? <FaFileExcel className="client-icon-excel" />
      : type === 'DOCX'
        ? <FaFileWord className="client-icon-pdf" />
        : <FaFilePdf className="client-icon-pdf" />;

  const years = Array.from(new Set(report.map(r => r.year))).sort((a, b) => b - a);

  // Función utilitaria para extraer grupo base del tipo_documento
  function getGroupBase(tipo: string) {
    if (!tipo) return "";
    const group = tipo
      .split(':')[0]
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[\s_-]/g, '');
    if (group.includes("contable")) return "contable";
    if (group.includes("presupuestal")) return "presupuestal";
    if (group.includes("programatica")) return "programatica";
    if (group.includes("informacionadicional")) return "informacionadicional";
    if (group.includes("disciplinafinanciera")) return "disciplinafinanciera";
    return "otros";
  }

  // Utilidad para normalizar el string del trimestre (acepta tanto 'trimestre' como 'quarters')
  function getTrimCode(f: FinancialFileRecord) {
    return (( f.quarters || '') + '').toUpperCase().replace(/\s/g, '');
  }

  return (
    <div className="client-nav-wrapper">
      <h2 className="client-nav-title" style={{ fontSize: '1.5rem' }}>Documentos Financieros</h2>
      {/* Verificación de reporte vacío completamente */}
      {report.length === 0 ? (
        <div style={{ color: "#7286ff", padding: 25, fontWeight: 600, fontSize: "1.2em", textAlign: 'center' }}>
          No hay documentos financieros disponibles.
        </div>
      ) : (
        <>
          <div className="client-tabs-container">
            {years.map(y => (
              <button
                key={y}
                className={`client-tab-btn ${openYear === y ? 'active' : ''}`}
                onClick={() => setOpenYear(openYear === y ? null : y)}
              >
                {y}
              </button>
            ))}
          </div>
          {years.map(y => {
            const entry = report.filter(r => r.year === y);
            const files = entry.flatMap(r => r.files || []);
            return (
              <div
                key={y}
                className={`client-section ${openYear === y ? 'open' : ''}`}
              >
                {openYear === y && files.length === 0 && (
                  <div style={{ color: "#7286ff", padding: 25, fontWeight: 600, fontSize: "1.2em" }}>
                    No hay archivos registrados para el año seleccionado.
                  </div>
                )}
                {openYear === y && files.length > 0 && (
                  <div className="client-quarter-list">
                    {quarters.map(({ code, label }) => {
                      // Filtrar archivos de este trimestre, flexible por campo
                      const qFiles = files.filter(f =>
                        normalizeQuarter(f.quarters || '') === code
                      );
                      if (!qFiles.length) return (
                        <div style={{ color: "#7286ff", margin: 10, padding: 2 }}>{`No hay archivos para ${label}`}</div>
                      );

                      // Agrupar por type_docs (pueden venir dos por type_docs+trimestre)
                      const docsMap: Record<string, FinancialFileRecord[]> = {};
                      qFiles.forEach(f => {
                        const key = f.type_docs || "";
                        if (key) {
                          if (!docsMap[key]) docsMap[key] = [];
                          docsMap[key].push(f);
                        }
                      });

                      // Agrupa los documentos por grupo base
                      const groupBlocks: Record<string, Array<{ tipo: string, arr: FinancialFileRecord[] }>> = {};
                      Object.entries(docsMap).forEach(([tipo, arr]) => {
                        const group = getGroupBase(tipo);
                        if (!groupBlocks[group]) groupBlocks[group] = [];
                        // Limita máximo a dos archivos (por si hay más accidentalmente)
                        groupBlocks[group].push({ tipo, arr: arr.slice(0, 2) });
                      });

                      return (
                        <div key={code} className="client-quarter-block">
                          <h3 className="client-quarter-title">{label}</h3>
                          {(['contable', 'presupuestal', 'programatica', 'informacionadicional', 'disciplinafinanciera', 'otros'] as const).map(grupoKey =>
                            groupBlocks[grupoKey]?.length ? (
                              <div key={grupoKey} style={{ marginBottom: '1rem', marginTop: grupoKey !== 'otros' ? 8 : 0 }}>
                                {GROUPS[grupoKey] && (
                                  <div style={{ fontSize: '1.09em', color: '#3745DE', fontWeight: 700, marginBottom: 7, marginLeft: 2 }}>
                                    {GROUPS[grupoKey]}
                                  </div>
                                )}
                                <div>
                                  {groupBlocks[grupoKey].map(({ tipo, arr }) => (
                                    <div key={tipo} style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 10,
                                      marginBottom: 7
                                    }}>
                                      {/* Mostramos SOLO la parte después de los dos puntos (:) */}
                                      <span className="client-file-label" style={{ minWidth: 140, fontWeight: 600, color: "#3945DE" }}>
                                        {tipo.includes(':') ? tipo.split(':').slice(1).join(':').trim() : tipo}
                                      </span>
                                      {/* Muestra ambos archivos si vienen dos */}
                                      {arr.map(file => (
                                        <a
                                          key={file.financial_file_id}
                                          href={useCleanPath(file.file_path)}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="client-file-item"
                                          title={tipo}
                                          style={{ minWidth: 42, marginRight: 7 }}
                                        >
                                          {iconFor(file.file_type)}
                                        </a>
                                      ))}
                                      {arr.length > 1 && (
                                        <span style={{
                                          color: "#7286ff",
                                          fontSize: "0.96em",
                                          marginLeft: 2,
                                          fontWeight: 500
                                        }}>
                                        </span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ) : null
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );

};
